import React, { useState, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { Button, Typography } from '@kep-ui-kit/ui-components';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Visible24Icon, Invisible24Icon } from '@kep-ui-kit/ui-icons';
import { PasswordRequirementsIndicator } from '../ui/password-requirements-indicator';
import {
  getRecaptchaToken,
  links, passwordLowerCaseRegex, passwordNumberRegex, passwordSpecialCharRegex, passwordUpperCaseRegex,
  responseTypes,
  routesPaths,
  sendFunnelAnalytics,
  subscriptionTypes
} from '@selfserve/react-utils';
import { useParams, useNavigate } from 'react-router-dom';
import { withErrorHandling, IWrappedComponentProps } from '@selfserve/react-hocs';
import {
  StyledLogo,
  StyledV2PageWrapper,
  StyledV2LogoRow,
  StyledV2PageBackground,
  StyledV2GlassCard,
  StyledV2Input,
  StyledV2Link,
  StyledV2Checkbox,
  StyledV2EyeButton
} from '@selfserve/react-ui';
import { useTranslation } from 'react-i18next';
import { useCreateAccountRequest } from '../../../../react-feature-create-account/src/lib/data';
import { useAuth, useStorage, storage, storageKeys, useQueryParams } from '@selfserve/react-hooks';

const StyledGlassCard = styled(StyledV2GlassCard)({
  maxWidth: '600px',
});

const StyledFormContainer = styled.div(({ theme }: { theme?: any }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  alignItems: 'flex-start',
}));

const StyledFieldsGroup = styled.div(({ theme }: { theme?: any }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  alignItems: 'flex-start',
}));

const StyledForm = styled.form(({ theme }: { theme?: any }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(6),
}));

const StyledFieldRow = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  width: '100%',
  '@media (max-width: 600px)': {
    flexDirection: 'column',
  },
}));

const StyledFieldGroup = styled.div(({ theme }: { theme?: any }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  flex: 1,
}));

const StyledPasswordInputWrapper = styled.div({
  position: 'relative',
  width: '100%',
});

const StyledPasswordInput = styled(StyledV2Input)({
  paddingRight: '40px',
  width: '100%',
  boxSizing: 'border-box',
});

// Button size XL which not exists in the current version of @kep-ui-kit/ui-components + color for disabled state
const StyledSubmitBtn = styled(Button)(({ theme }: { theme?: any }) => ({
  fontSize: '16px',
  height: '48px',
  padding: '16px',
  '&:disabled, &.Mui-disabled': {
    backgroundColor: theme.palette.grey['400'],
    color: '#fff',
  },
}));

const StyledCheckboxWrapper = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
}));

export interface CreateAccountFormV2Values {
  firstName?: string;
  lastName?: string;
  companyName: string;
  password?: string;
  agreeToUpdates: boolean;
  allowReceivingMarketingData?: boolean;
}

// Props for the public wrapped component (without HOC props)
export interface CreateAccountFormV2Props {
  onSuccess?: (data: {
    partnerId?: number;
    subscriptionId?: string;
    loginUrl?: string;
  }) => void;
}

// Props for the internal component (with HOC props)
interface CreateAccountFormV2ComponentProps extends CreateAccountFormV2Props, IWrappedComponentProps {}

const CreateAccountFormV2Component: React.FC<CreateAccountFormV2ComponentProps> = ({
  openErrorModal,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const { setToken } = useAuth();
  const [existingUser, setExistingUser] = useStorage(storage.LOCAL, storageKeys.EXISTING_USER);
  const [isUserExist] = useState(existingUser);
  const [, setPartnerId] = useStorage(storage.LOCAL, storageKeys.PARTNER_ID);
  const [, setLoginUrl] = useStorage(storage.LOCAL, storageKeys.LOGIN_URL, '');
  const [, setEmailHash] = useStorage(storage.LOCAL, storageKeys.EMAIL_HASH, '');
  const [, setAvatarWizard] = useStorage(storage.LOCAL, storageKeys.AVATAR_WIZARD, '');
  const [, setSubscriptionId] = useStorage(storage.SESSION, storageKeys.SUBSCRIPTION_ID);

  const queryParams = useQueryParams(); // { socialRegister: true }
  const isSocialRegister = useMemo(() => {
    return queryParams['socialRegister'] === 'true';
  }, [queryParams]);

  const initialValues = useMemo(() => {
    const basePayload = {
      agreeToUpdates: false,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      allowReceivingMarketingData: true,
      companyName: '',
    }
    return isSocialRegister ? {
      ...basePayload
    } : {
      ...basePayload,
      firstName: '',
      lastName: '',
      password: '',
    }
  }, [isSocialRegister]);

  const {
    mutateAsync: handleCreateAccountRequest,
    data: createAccountResponse,
    isLoading: createAccountLoading,
  } = useCreateAccountRequest(initialValues, token as string);

  const validationSchema = useMemo(() => {
    // Social register only requires company name and checkbox
    if (isSocialRegister) {
      return yup.object({
        companyName: yup.string().required(t('create-account-v2.validations.company-name-required')),
        agreeToUpdates: yup.boolean().required().equals([true]),
      });
    }

    // Regular registration requires all fields
    return yup.object({
      firstName: yup.string().required(t('create-account-v2.validations.first-name-required')),
      lastName: yup.string().required(t('create-account-v2.validations.last-name-required')),
      companyName: yup.string().required(t('create-account-v2.validations.company-name-required')),
      ...(!isUserExist && {
        password: yup
          .string()
          .required(t('create-account-v2.validations.password-required'))
          .min(8, t('create-account-v2.validations.password-min'))
          .matches(passwordNumberRegex, t('create-account-v2.validations.password-number'))
          .matches(
            passwordSpecialCharRegex,
            t('create-account-v2.validations.password-special-char')
          )
          .matches(passwordLowerCaseRegex, t('create-account-v2.validations.password-lowercase'))
          .matches(passwordUpperCaseRegex, t('create-account-v2.validations.password-uppercase')),
      }),
      agreeToUpdates: yup.boolean().required().equals([true]),
    })
  }, [t, isUserExist, isSocialRegister]);

  const formik = useFormik<CreateAccountFormV2Values>({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setIsRecaptchaLoading(true);
      if (!Object.keys(errors).length && !createAccountLoading && !isRecaptchaLoading) {
        try {
          // Step 1: Get recaptcha token
          const recaptchaToken = await getRecaptchaToken();
          setIsRecaptchaLoading(false);

          // Step 2: Prepare payload (exclude agreeToUpdates, map companyName to company)
          const { agreeToUpdates, companyName, password, ...data } = values;
          const payload = {
            ...data,
            company: companyName, // Map companyName to company for API
            ...(!isUserExist && { password }), // Only include password if user doesn't exist
            recaptchaToken,
          };

          // Step 3: Call create account API
          const resp = await handleCreateAccountRequest(payload);

          // Step 4: Handle error response
          if (!String(resp.status).startsWith(responseTypes.SUCCESS)) {
            const errorId = resp?.data?.id;
            const errorTitleName = `error-titles.${errorId}`;
            const title = i18n.exists(errorTitleName) ? t(errorTitleName) : undefined;
            openErrorModal({
              text: i18n.exists(`errors.${errorId}`)
                ? t(`errors.${errorId}`)
                : t('common.purchase-error'),
              title,
            });
          }
        } catch (error) {
          setIsRecaptchaLoading(false);
          const errorMessage =
            error instanceof Error ? error.message : 'Account creation failed';
          openErrorModal({ text: errorMessage });
        }
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isValid,
    dirty,
  } = formik;

  const formType = useMemo(() => {
    const socialType = sessionStorage.getItem(storageKeys.SOCIAL_TYPE) || 'unknown';
    return isSocialRegister ? socialType : 'full';
  }, [isSocialRegister]);

  // Handle successful account creation response
  useEffect(() => {
    if (!createAccountResponse) return;
    const { data, status } = createAccountResponse;
    const {
      access_token,
      subscriptionType,
      subscriptionId,
      url,
      loginUrl,
      emailHash,
      avatarWizard,
      partner,
    } = data || {};

    if (String(status).startsWith(responseTypes.SUCCESS)) {
      sendFunnelAnalytics({
        type: 'formSubmitted',
        page: '',
        event: 'createAccountSuccess',
        properties: {
          formType
        }
      }, null, true);
      setExistingUser(false); // Clear existing user flag on successful account creation
      if (access_token) setToken(access_token);
      if (partner?.id) setPartnerId(partner?.id);

      if (subscriptionType === subscriptionTypes.PAID) {
        const redirectUrl = routesPaths.BUILD_PLAN.replace(':hash', `${token}`);
        navigate(redirectUrl);
      } else {
        if (loginUrl) setLoginUrl(loginUrl);
        if (emailHash) setEmailHash(emailHash);
        if (avatarWizard) setAvatarWizard('true');
        if (subscriptionId) setSubscriptionId(subscriptionId);
        if (url) {
          window.location.href = url;
        } else {
          const redirectUrl = routesPaths.CONFIRMED.replace(
            ':subscriptionId',
            subscriptionId as string
          );
          navigate(redirectUrl);
        }
      }
    }
  }, [
    createAccountResponse,
    navigate,
    token,
    setToken,
    setPartnerId,
    setLoginUrl,
    setEmailHash,
    setAvatarWizard,
    setSubscriptionId,
    setExistingUser,
  ]);

  useEffect(() => {
    sendFunnelAnalytics({
      type: 'pageLoad',
      page: '',
      event: 'createAccountForm',
      properties: {
        formType
      }
    });
  }, []);

  return (
    <StyledV2PageWrapper>
      <StyledV2LogoRow>
        <StyledLogo alt="Kaltura" />
      </StyledV2LogoRow>
      <StyledV2PageBackground centered className="container">
        <StyledGlassCard>
          <Typography variant="h2" textAlign={'center'}>
            {t('create-account-v2.form-title')}
          </Typography>

          <StyledForm onSubmit={handleSubmit}>
            <StyledFormContainer>
              <StyledFieldsGroup>
                {/* Social Register: Show only company name field */}
                {isSocialRegister ? (
                  <StyledFieldGroup>
                    <Typography variant={"body1Highlight" as any}>
                      {t('create-account-v2.company-name')}
                    </Typography>
                    <StyledV2Input
                      id="companyName"
                      name="companyName"
                      type="text"
                      value={values.companyName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      hasError={touched.companyName && !!errors.companyName}
                      autoComplete="organization"
                    />
                    {touched.companyName && errors.companyName && (
                      <Typography variant={"formError" as any} sx={{ mt: 0.5, color: (theme: any) => theme.palette.danger.main, fontWeight: 700 }}>
                        {errors.companyName}
                      </Typography>
                    )}
                  </StyledFieldGroup>
                ) : (
                  <>
                    {/* Regular Register: Show all fields */}
                    <StyledFieldRow>
                      <StyledFieldGroup>
                        <Typography variant={"body1Highlight" as any}>
                          {t('create-account-v2.first-name')}
                        </Typography>
                        <StyledV2Input
                          id="firstName"
                          name="firstName"
                          type="text"
                          value={values.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          hasError={touched.firstName && !!errors.firstName}
                          autoComplete="given-name"
                        />
                        {touched.firstName && errors.firstName && (
                          <Typography variant={"formError" as any} sx={{ mt: 0.5, color: (theme: any) => theme.palette.danger.main, fontWeight: 700 }}>
                            {errors.firstName}
                          </Typography>
                        )}
                      </StyledFieldGroup>

                      <StyledFieldGroup>
                        <Typography variant={"body1Highlight" as any}>
                          {t('create-account-v2.last-name')}
                        </Typography>
                        <StyledV2Input
                          id="lastName"
                          name="lastName"
                          type="text"
                          value={values.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          hasError={touched.lastName && !!errors.lastName}
                          autoComplete="family-name"
                        />
                        {touched.lastName && errors.lastName && (
                          <Typography variant={"formError" as any} sx={{ mt: 0.5, color: (theme: any) => theme.palette.danger.main, fontWeight: 700 }}>
                            {errors.lastName}
                          </Typography>
                        )}
                      </StyledFieldGroup>
                    </StyledFieldRow>

                    <StyledFieldGroup>
                      <Typography variant={"body1Highlight" as any}>
                        {t('create-account-v2.company-name')}
                      </Typography>
                      <StyledV2Input
                        id="companyName"
                        name="companyName"
                        type="text"
                        value={values.companyName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        hasError={touched.companyName && !!errors.companyName}
                        autoComplete="organization"
                      />
                      {touched.companyName && errors.companyName && (
                        <Typography variant={"formError" as any} sx={{ mt: 0.5, color: (theme: any) => theme.palette.danger.main, fontWeight: 700 }}>
                          {errors.companyName}
                        </Typography>
                      )}
                    </StyledFieldGroup>

                    {!isUserExist && (
                      <>
                        <StyledFieldGroup>
                          <Typography variant={"body1Highlight" as any}>
                            {t('create-account-v2.password')}
                          </Typography>
                          <StyledPasswordInputWrapper>
                            <StyledPasswordInput
                              id="password"
                              name="password"
                              type={showPassword ? 'text' : 'password'}
                              value={values.password}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              hasError={touched.password && !!errors.password}
                              autoComplete="new-password"
                            />
                            <StyledV2EyeButton
                              variant={'borderless'}
                              color={'secondary'}
                              onClick={() => setShowPassword(!showPassword)}
                              aria-label={showPassword ? t('create-account-v2.hide-password') : t('create-account-v2.show-password')}
                            >
                              {showPassword ? <Invisible24Icon /> : <Visible24Icon />}
                            </StyledV2EyeButton>
                          </StyledPasswordInputWrapper>

                          <PasswordRequirementsIndicator
                            password={values.password || ''}
                            touched={touched.password || !!values.password}
                          />
                        </StyledFieldGroup>
                      </>
                    )}
                  </>
                )}
              </StyledFieldsGroup>

              {/* Common fields for both social and regular register */}
              <StyledCheckboxWrapper>
                <StyledV2Checkbox
                  id="agreeToUpdates"
                  name="agreeToUpdates"
                  type="checkbox"
                  checked={values.agreeToUpdates}
                  onChange={handleChange}
                />
                <Typography variant="body1">
                  {t('create-account-v2.agree-to-updates')}
                </Typography>
              </StyledCheckboxWrapper>

              <Typography variant="body1"textAlign="left">
                {t('create-account-v2.by-clicking-create-account')}{' '}
                <StyledV2Link href={links.SERVICE_AGREEMENT} target="_blank" rel="noopener noreferrer">
                  {t('common.service-agreement')}
                </StyledV2Link>{' '}
                {t('common.and')}{' '}
                <StyledV2Link href={links.PRIVACY_POLICY} target="_blank" rel={"noopener noreferrer"}>
                  {t('common.privacy-policy')}
                </StyledV2Link>
              </Typography>
            </StyledFormContainer>

            <StyledSubmitBtn
              type="submit"
              loading={createAccountLoading || isRecaptchaLoading}
              disabled={!isValid || !dirty}
              onClick={() => {
                sendFunnelAnalytics({
                  type: 'buttonClicked',
                  page: '',
                  event: 'createAccountSubmit',
                  properties: {
                    formType
                  }
                });
              }}
            >
              {t('create-account-v2.create-account-btn')}
            </StyledSubmitBtn>
          </StyledForm>
        </StyledGlassCard>
      </StyledV2PageBackground>
    </StyledV2PageWrapper>
  );
};

export const CreateAccountFormV2 = withErrorHandling<CreateAccountFormV2Props>(CreateAccountFormV2Component);
